import {
  DomUtil,
  Draggable,
  Evented,
  LatLng,
  LeafletEvent,
  Point,
  Popup as LeafletPopup,
  PopupEvent,
} from 'leaflet';
import {ReactNode, useEffect, useRef} from 'react';
import {Popup, PopupProps, useMap} from 'react-leaflet';

const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, label, [role="button"], [contenteditable="true"]';

type PopupWithSource = LeafletPopup & {_source?: Evented};

interface Props extends PopupProps {
  children?: ReactNode;
}

const DraggablePopup = ({children, autoPan = false, ...props}: Props) => {
  const map = useMap();
  const popupRef = useRef<LeafletPopup | null>(null);

  useEffect(() => {
    let draggable: Draggable | null = null;
    let container: HTMLElement | null = null;
    let startPos: Point | null = null;
    let startLatLng: LatLng | null = null;
    let draggedLatLng: LatLng | null = null;
    let source: Evented | null = null;

    const reanchor = () => {
      if (popupRef.current && draggedLatLng) {
        popupRef.current.setLatLng(draggedLatLng);
      }
    };

    const getTip = () =>
      container?.querySelector<HTMLElement>('.leaflet-popup-tip-container');

    const setTipVisible = (visible: boolean) => {
      const tip = getTip();
      if (tip) {
        tip.style.display = visible ? '' : 'none';
      }
    };

    const suppressNextClick = () => {
      const target = container;
      if (!target) {
        return;
      }
      const onClick = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
      };
      target.addEventListener('click', onClick, {capture: true, once: true});
      window.setTimeout(() => {
        target.removeEventListener('click', onClick, true);
      }, 0);
    };

    const onMouseDownCapture = (event: MouseEvent) => {
      if (!draggable) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.closest(INTERACTIVE_SELECTOR)) {
        draggable.disable();
      } else {
        draggable.enable();
      }
    };

    const onDragStart = () => {
      if (!container || !popupRef.current) {
        return;
      }
      startPos = DomUtil.getPosition(container);
      startLatLng = popupRef.current.getLatLng() ?? null;
    };

    const onDragEnd = () => {
      const popup = popupRef.current;
      if (!container || !popup || !startPos || !startLatLng) {
        return;
      }

      const delta = DomUtil.getPosition(container).subtract(startPos);
      DomUtil.setPosition(container, startPos);

      draggedLatLng = map.layerPointToLatLng(
        map.latLngToLayerPoint(startLatLng).add(delta)
      );
      popup.setLatLng(draggedLatLng);

      setTipVisible(false);
      suppressNextClick();

      if (!source) {
        source = (popup as PopupWithSource)._source ?? null;
        source?.on('move', reanchor);
      }
    };

    const detach = () => {
      draggable?.disable();
      draggable?.off('dragstart', onDragStart);
      draggable?.off('dragend', onDragEnd);
      container?.removeEventListener('mousedown', onMouseDownCapture, true);
      source?.off('move', reanchor);
      draggable = null;
      container = null;
      startPos = null;
      startLatLng = null;
      draggedLatLng = null;
      source = null;
    };

    const onPopupOpen = (event: PopupEvent) => {
      if (event.popup !== popupRef.current) {
        return;
      }
      detach();

      container = popupRef.current?.getElement() ?? null;
      if (!container) {
        return;
      }

      setTipVisible(true);

      draggable = new Draggable(container);
      draggable.on('dragstart', onDragStart as (e: LeafletEvent) => void);
      draggable.on('dragend', onDragEnd as (e: LeafletEvent) => void);
      draggable.enable();

      container.addEventListener('mousedown', onMouseDownCapture, true);
    };

    const onPopupClose = (event: PopupEvent) => {
      if (event.popup !== popupRef.current) {
        return;
      }
      detach();
    };

    map.on('popupopen', onPopupOpen);
    map.on('popupclose', onPopupClose);

    return () => {
      map.off('popupopen', onPopupOpen);
      map.off('popupclose', onPopupClose);
      detach();
    };
  }, [map]);

  return (
    <Popup ref={popupRef} autoPan={autoPan} {...props}>
      {children}
    </Popup>
  );
};

export default DraggablePopup;
